import {
  AppWindow,
  CircleEllipsis,
  GitBranch,
  LayoutPanelTop,
  Mail,
  MessageCircle,
} from 'lucide-react';
import { ComponentType, CSSProperties } from 'react';
import {
  SiCaldotcom,
  SiCalendly,
  SiDiscord,
  SiHubspot,
  SiInstagram,
  SiMessenger,
  SiShopify,
  SiStripe,
  SiTelegram,
  SiWhatsapp,
  SiZendesk,
} from 'react-icons/si';
import { IndexPath } from '@/Config/app/router/index.path';
import { IntercomIcon } from '@/Domain/components/icons/Intercom.icon';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { SlackIcon } from '@/Domain/components/icons/Slack.icon';
import { cn } from '@/Domain/utils/cn';
import { WEBSITE_VIEW_QUERY_PARAM, WebsiteView } from '@/UI/Website/viewMode';

type Glyph = ComponentType<{ className?: string; style?: CSSProperties }>;

export const OPEN_MODAL_QUERY_PARAM = 'open';

export const CHANNEL_CHAT_WIDGET = 'chat_widget';
export const CHANNEL_CHAT_EMBED = 'chat_embed';
export const CHANNEL_CHAT_PAGE = 'chat_page';
export const CHANNEL_SHOPIFY = 'shopify';
export const CHANNEL_HUBSPOT = 'hubspot';
export const CHANNEL_INTERCOM = 'intercom';
export const CHANNEL_WHATSAPP = 'whatsapp';
export const CHANNEL_INSTAGRAM = 'instagram';
export const CHANNEL_MESSENGER = 'messenger';
export const CHANNEL_TELEGRAM = 'telegram';
export const CHANNEL_DISCORD = 'discord';
export const CHANNEL_SLACK = 'slack';
export const CHANNEL_ZENDESK = 'zendesk';
export const CHANNEL_EMAIL = 'email';
export const CHANNEL_MCP = 'mcp';
export const CHANNEL_API = 'api';
export const CHANNEL_OTHER_ID = 'other';

export const APP_HUBSPOT = 'hubspot';
export const APP_SHOPIFY = 'shopify';
export const APP_GOOGLE_SHEETS = 'google_sheets';
export const APP_STRIPE = 'stripe';
export const APP_CALDOTCOM = 'caldotcom';
export const APP_CALENDLY = 'calendly';
export const APP_OTHER_MCP = 'other_mcp';
export const APP_OTHER_API_ID = 'other_api';

const ApiGlyph: Glyph = ({ className, style }) => <GitBranch className={className} style={style} />;

const McpGlyph: Glyph = ({ className, style }) => <McpIcon className={className} style={style} />;

// react-icons/si only ships the full Cal.com wordmark; clip ".com" via overflow-hidden.
const CalMark: Glyph = ({ className, style }) => (
  <span
    className={cn('inline-flex items-center overflow-hidden flex-shrink-0', className)}
    style={{ ...style, width: '19px', height: '16px' }}
  >
    <SiCaldotcom style={{ height: '48px', width: '48px', flexShrink: 0 }} />
  </span>
);

export interface ChannelOption {
  id: string;
  label: string;
  icon: Glyph;
  // Optional: Domain/components/icons/* use baked-in fills and ignore color.
  iconColor?: string;
  routePath: string;
  /** When true, "Set up" appends ?open=<id> for the destination page's auto-open handler. */
  autoOpenModal: boolean;
  /** Extra query-string params merged into the destination URL (e.g. view=embed). */
  queryParams?: Record<string, string>;
}

export const CHANNELS: readonly ChannelOption[] = [
  {
    id: CHANNEL_CHAT_WIDGET,
    label: 'Chat widget',
    icon: MessageCircle,
    routePath: IndexPath.website,
    autoOpenModal: false,
  },
  {
    id: CHANNEL_CHAT_EMBED,
    label: 'Chat embed',
    icon: AppWindow,
    routePath: IndexPath.website,
    autoOpenModal: false,
    queryParams: { [WEBSITE_VIEW_QUERY_PARAM]: WebsiteView.Embed },
  },
  {
    id: CHANNEL_CHAT_PAGE,
    label: 'Chat page',
    icon: LayoutPanelTop,
    routePath: IndexPath.website,
    autoOpenModal: false,
    queryParams: { [WEBSITE_VIEW_QUERY_PARAM]: WebsiteView.ChatPage },
  },
  {
    id: CHANNEL_SHOPIFY,
    label: 'Shopify',
    icon: SiShopify,
    iconColor: '#95BF47',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_HUBSPOT,
    label: 'HubSpot',
    icon: SiHubspot,
    iconColor: '#FF7A59',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_INTERCOM,
    label: 'Intercom',
    icon: IntercomIcon,
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_WHATSAPP,
    label: 'WhatsApp',
    icon: SiWhatsapp,
    iconColor: '#25D366',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_INSTAGRAM,
    label: 'Instagram',
    icon: SiInstagram,
    iconColor: '#E4405F',
    routePath: IndexPath.channels,
    // No dedicated SPA setup sheet: "Set up" lands on the channels overview instead of a no-op deep link.
    autoOpenModal: false,
  },
  {
    id: CHANNEL_MESSENGER,
    label: 'Messenger',
    icon: SiMessenger,
    iconColor: '#00B2FF',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_TELEGRAM,
    label: 'Telegram',
    icon: SiTelegram,
    iconColor: '#26A5E4',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_DISCORD,
    label: 'Discord',
    icon: SiDiscord,
    iconColor: '#5865F2',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_SLACK,
    label: 'Slack',
    icon: SlackIcon,
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_ZENDESK,
    label: 'Zendesk',
    icon: SiZendesk,
    iconColor: '#03363D',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_EMAIL,
    label: 'Email',
    icon: Mail,
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_MCP,
    label: 'MCP',
    icon: McpGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_API,
    label: 'API',
    icon: ApiGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_OTHER_ID,
    label: 'Other',
    icon: CircleEllipsis,
    routePath: IndexPath.channels,
    // No dedicated SPA setup sheet: "Set up" lands on the channels overview instead of a no-op deep link.
    autoOpenModal: false,
  },
] as const;

export interface AppOption {
  id: string;
  label: string;
  icon: Glyph;
  iconColor: string;
  routePath: string;
  autoOpenModal: boolean;
}

export const APPS: readonly AppOption[] = [
  {
    id: APP_HUBSPOT,
    label: 'HubSpot',
    icon: SiHubspot,
    iconColor: '#FF7A59',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_SHOPIFY,
    label: 'Shopify',
    icon: SiShopify,
    iconColor: '#95BF47',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_STRIPE,
    label: 'Stripe',
    icon: SiStripe,
    iconColor: '#635BFF',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_CALDOTCOM,
    label: 'Cal.com',
    icon: CalMark,
    iconColor: 'var(--foreground)',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_CALENDLY,
    label: 'Calendly',
    icon: SiCalendly,
    iconColor: '#006BFF',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_OTHER_MCP,
    label: 'Other MCP',
    icon: McpGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_OTHER_API_ID,
    label: 'Other API',
    icon: ApiGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
] as const;
